/**
 * @file AssessmentQuestionStackedOption — a single numbered circle option within a stacked question
 * @version 0.2.2
 */

import React, { forwardRef } from 'react';
import styles from './AssessmentQuestionStackedOption.module.css';

/*
 * AssessmentQuestionStackedOption — a single numbered circle button, one
 * selectable dot in a stacked Likert scale row.
 *
 * A 48×48 circle with a centered number and three runtime states:
 *   - default:  translucent teal-grey circle, light number.
 *   - hover:    diagonal teal gradient + cyan border + outer cyan glow.
 *   - selected: bright-teal gradient + dark ink number.
 * A focus-visible ring is layered on top for keyboard users (cyan on the
 * navy/hover surfaces, dark on the bright-teal selected surface).
 *
 * An optional value label (`hoverLabel`) names the number's meaning on the
 * scale. It reveals under the circle on hover and keyboard focus, and is
 * folded into the accessible name permanently — a hover-only reveal doesn't
 * exist for screen readers.
 *
 * Renders as <button role="radio">. Its parent scale is the radiogroup and
 * owns roving focus + arrow-key navigation; this control forwards its ref and
 * accepts `tabIndex` / `onKeyDown` so the group can drive it.
 */

export interface AssessmentQuestionStackedOptionProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-checked'> {
  /** The number shown inside the circle. */
  number: number;
  /**
   * Optional label naming the number's meaning (e.g. "Occasionally").
   * Reveals under the circle on hover/focus; always part of the
   * accessible name.
   */
  hoverLabel?: string;
  /** Picked state — bright-teal gradient + dark ink when true. */
  selected?: boolean;
  /** Disabled — dims the circle, mutes the number, blocks interaction. */
  disabled?: boolean;
  /**
   * Colour treatment. `dark` (default) is the filled translucent circle; `light`
   * is a transparent circle with a navy ring, navy number, teal-gradient hover,
   * and solid-navy selected. Usually supplied by the parent card. Per-instance variant.
   */
  theme?: 'light' | 'dark';
  /** Click handler. Fires when the option is picked. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Forwarded to the root button. */
  className?: string;
  /** Forces the hover visual on at-rest circles (static-state preview). */
  'data-force-hover'?: boolean;
  /** Forces the focus-visible ring on at-rest circles (static-state preview). */
  'data-force-focus'?: boolean;
}

export const AssessmentQuestionStackedOption = forwardRef<
  HTMLButtonElement,
  AssessmentQuestionStackedOptionProps
>(function AssessmentQuestionStackedOption(
  {
    number,
    hoverLabel,
    selected = false,
    disabled = false,
    theme = 'dark',
    onClick,
    className,
    'data-force-hover': dataForceHover,
    'data-force-focus': dataForceFocus,
    ...rest
  },
  ref,
) {
  const classes = [
    styles.option,
    theme === 'light' ? styles.light : '',
    selected ? styles.selected : '',
    disabled ? styles.disabled : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={hoverLabel ? `${number}, ${hoverLabel}` : undefined}
      className={classes}
      onClick={onClick}
      disabled={disabled}
      data-force-hover={dataForceHover ? '' : undefined}
      data-force-focus={dataForceFocus ? '' : undefined}
      {...rest}
    >
      <span className={styles.number}>{number}</span>
      {hoverLabel !== undefined && (
        <span className={styles.hoverLabel} aria-hidden="true">
          {hoverLabel}
        </span>
      )}
    </button>
  );
});
